import React, { useMemo, useState } from 'react';
import { Search, ListChecks } from 'lucide-react';
import { ROLES } from '../data/framework';
import { ITEM_TYPE_LABEL, isStandard } from '../utils/framework';
import type { FrameworkItem, ItemType } from '../types/framework';
type Row = FrameworkItem & {
  roleId: string;
  roleName: string;
  domainId: string;
  domainName: string;
  standard: boolean;
};
const ALL_ROWS: Row[] = ROLES.flatMap(role => role.domains.flatMap(domain => [...domain.characteristic, ...domain.behaviour].map(item => ({
  ...item,
  roleId: role.id,
  roleName: role.name,
  domainId: domain.id,
  domainName: domain.name,
  standard: isStandard(domain, item)
}))));
const PAGE_SIZE = 50;
export function QuestionLibrary() {
  const [search, setSearch] = useState('');
  const [roleId, setRoleId] = useState('all');
  const [domainId, setDomainId] = useState('all');
  const [type, setType] = useState<ItemType | 'all'>('all');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const role = ROLES.find(r => r.id === roleId);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return ALL_ROWS.filter(r => {
      if (roleId !== 'all' && r.roleId !== roleId) return false;
      if (domainId !== 'all' && r.domainId !== domainId) return false;
      if (type !== 'all' && r.type !== type) return false;
      if (q && !r.criterion.toLowerCase().includes(q) && !r.competency.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [search, roleId, domainId, type]);
  const resetPaging = () => setVisible(PAGE_SIZE);
  const hasFilters = search !== '' || roleId !== 'all' || domainId !== 'all' || type !== 'all';
  const clearFilters = () => {
    setSearch('');
    setRoleId('all');
    setDomainId('all');
    setType('all');
    resetPaging();
  };
  const selectClass = 'px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900';
  return <div className="animate-in fade-in duration-500 space-y-6" data-id="element-1928" data-mp="3faxdn60qw9sq">
      <div data-id="element-1929" data-mp="3k8p2tsa09ht8">
        <h1 className="text-2xl font-semibold text-slate-900" data-id="element-1930" data-mp="3k9gy11zoxdiz">
          Question Library
        </h1>
        <p className="text-slate-500 mt-1" data-id="element-1931" data-mp="369phathshj1a">
          Every Characteristic and Behaviour statement in the competency
          framework — {ALL_ROWS.length.toLocaleString()} questions across{' '}
          {ROLES.length} roles.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-3" data-id="element-1932" data-mp="6tizutspt1a4">
        <div className="relative" data-id="element-1933" data-mp="3p78odrkosrl2">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-1934" data-mp="3p67dpo7l273v" />
          <input type="text" value={search} onChange={e => {
          setSearch(e.target.value);
          resetPaging();
        }} aria-label="Search questions" placeholder="Search by competency or statement..." className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900" data-id="element-1935" data-mp="3u5a9ei1ob0z6" />
        </div>
        <div className="flex flex-col md:flex-row gap-3" data-id="element-1936" data-mp="19lc29xccuq78">
          <select aria-label="Role" value={roleId} onChange={e => {
          setRoleId(e.target.value);
          setDomainId('all');
          resetPaging();
        }} className={`${selectClass} md:w-64`} data-id="element-1937" data-mp="19ktx2b7pt1n6">
            <option value="all" data-id="element-1938" data-mp="cwnwbzdwyte6">All roles</option>
            {ROLES.map(r => <option key={r.id} value={r.id} data-id="element-1939" data-mp="qw5fnz8fxeg5">
                {r.name}
              </option>)}
          </select>
          <select aria-label="Competency domain" value={domainId} disabled={!role} onChange={e => {
          setDomainId(e.target.value);
          resetPaging();
        }} className={`${selectClass} md:flex-1 disabled:bg-slate-50 disabled:text-slate-400`} data-id="element-1940" data-mp="lug5elt1l4t4">
            <option value="all" data-id="element-1941" data-mp="bsx938r442q7">
              {role ? 'All domains' : 'Choose a role to filter by domain'}
            </option>
            {role?.domains.map(d => <option key={d.id} value={d.id} data-id="element-1942" data-mp="gucx99ol6pct">
                {d.name}
              </option>)}
          </select>
          <div className="inline-flex rounded-md border border-slate-300 p-0.5 bg-slate-50 shrink-0" role="group" aria-label="Question type" data-id="element-1943" data-mp="pt80ccwc3jbw">
            {(['all', 'characteristic', 'behaviour'] as const).map(t => <button key={t} onClick={() => {
            setType(t);
            resetPaging();
          }} aria-pressed={type === t} className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${type === t ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`} data-id="element-1944" data-mp="zkukiyqngsqs">
                {t === 'all' ? 'All' : ITEM_TYPE_LABEL[t]}
              </button>)}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-slate-500" data-id="element-1945" data-mp="2ozlopco1sh1c">
        <span data-id="element-1946" data-mp="2p1q2cmbr0uet">
          {filtered.length.toLocaleString()}{' '}
          {filtered.length === 1 ? 'question' : 'questions'}
        </span>
        {hasFilters && <button onClick={clearFilters} className="font-medium text-slate-700 hover:text-slate-900" data-id="element-1947" data-mp="3gqjf2a2xzpjn">
            Clear filters
          </button>}
      </div>

      {filtered.length === 0 ? <div className="border border-dashed border-slate-300 rounded-xl py-16 text-center bg-white" data-id="element-1948" data-mp="32zo50ugpj2eh">
          <ListChecks className="h-6 w-6 text-slate-400 mx-auto mb-3" data-id="element-1949" data-mp="49e8joltrqtv" />
          <h3 className="text-sm font-medium text-slate-900" data-id="element-1950" data-mp="4br1ewt4fllp">
            No questions found
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1951" data-mp="3vmha1p14qnua">
            Try a different search or clear the filters.
          </p>
        </div> : <div className="bg-white border border-slate-200 rounded-xl shadow-sm divide-y divide-slate-100" data-id="element-1952" data-mp="nd7ztg6zo6l1">
          {filtered.slice(0, visible).map(r => <div key={`${r.roleId}-${r.no}`} className="p-4 sm:p-5" data-id="element-1953" data-mp="dog9fchktuc8">
              <div className="flex flex-wrap items-center gap-2 mb-1.5" data-id="element-1954" data-mp="dmn543ufrm7e">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${r.type === 'characteristic' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' : 'bg-teal-50 text-teal-700 border-teal-100'}`} data-id="element-1955" data-mp="3vtb6bimd7p7s">
                  {ITEM_TYPE_LABEL[r.type]}
                </span>
                <span className="text-xs text-slate-500" data-id="element-1956" data-mp="3qv9of10odvxw">
                  {r.roleName} · {r.domainName}
                </span>
                {r.standard && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100" data-id="element-1957" data-mp="1p3wn0lxx266l">
                    Standard
                  </span>}
                <span className="text-xs text-slate-400 ml-auto" data-id="element-1958" data-mp="35l3n7j62hk2u">#{r.no}</span>
              </div>
              <p className="text-sm text-slate-900 leading-snug" data-id="element-1959" data-mp="3jcihwn9c79pq">
                {r.criterion}
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-1960" data-mp="3ej79wissgmw8">{r.competency}</p>
            </div>)}
          {visible < filtered.length && <div className="p-4 text-center" data-id="element-1961" data-mp="3oajzf3r470z3">
              <button onClick={() => setVisible(v => v + PAGE_SIZE)} className="text-sm font-medium text-slate-700 border border-slate-200 px-4 py-2 rounded-md hover:bg-slate-50" data-id="element-1962" data-mp="3o9s5ryh2vdbg">
                Show more ({(filtered.length - visible).toLocaleString()}{' '}
                remaining)
              </button>
            </div>}
        </div>}
    </div>;
}